import { useReducer, useCallback } from 'react';
import type { KnowledgeItem, Finding } from '../../../types';

type ModalName =
  | 'accountManagement'
  | 'newInvestigationScope'
  | 'createInvestigation';

interface ModalState {
  accountManagement: boolean;
  newInvestigationScope: boolean;
  createInvestigation: boolean;
  selectedKnowledgeItem: KnowledgeItem | null;
  selectedFinding: Finding | null;
}

type ModalAction =
  | { type: 'OPEN'; name: ModalName }
  | { type: 'CLOSE'; name: ModalName }
  | { type: 'VIEW_KNOWLEDGE_ITEM'; item: KnowledgeItem }
  | { type: 'CLOSE_KNOWLEDGE_ITEM' }
  | { type: 'VIEW_FINDING'; finding: Finding }
  | { type: 'CLOSE_FINDING' };

const initialState: ModalState = {
  accountManagement: false,
  newInvestigationScope: false,
  createInvestigation: false,
  selectedKnowledgeItem: null,
  selectedFinding: null,
};

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'OPEN':
      return { ...state, [action.name]: true };
    case 'CLOSE':
      return { ...state, [action.name]: false };
    case 'VIEW_KNOWLEDGE_ITEM':
      return { ...state, selectedKnowledgeItem: action.item };
    case 'CLOSE_KNOWLEDGE_ITEM':
      return { ...state, selectedKnowledgeItem: null };
    case 'VIEW_FINDING':
      return { ...state, selectedFinding: action.finding };
    case 'CLOSE_FINDING':
      return { ...state, selectedFinding: null };
    default:
      return state;
  }
}

export function useInvestigationModals() {
  const [modalState, dispatch] = useReducer(modalReducer, initialState);

  const openModal = useCallback((name: ModalName) => {
    dispatch({ type: 'OPEN', name });
  }, []);

  const closeModal = useCallback((name: ModalName) => {
    dispatch({ type: 'CLOSE', name });
  }, []);

  const viewKnowledgeItem = useCallback((item: KnowledgeItem) => {
    dispatch({ type: 'VIEW_KNOWLEDGE_ITEM', item });
  }, []);

  const closeKnowledgeItem = useCallback(() => {
    dispatch({ type: 'CLOSE_KNOWLEDGE_ITEM' });
  }, []);

  const viewFinding = useCallback((finding: Finding) => {
    dispatch({ type: 'VIEW_FINDING', finding });
  }, []);

  const closeFinding = useCallback(() => {
    dispatch({ type: 'CLOSE_FINDING' });
  }, []);

  return {
    modalState,
    openModal,
    closeModal,
    viewKnowledgeItem,
    closeKnowledgeItem,
    viewFinding,
    closeFinding,
  };
}
